<script lang="ts" setup>
  import type { Licence } from '~/types/licence'
  import { CloseIcon, LoadingArrowsIcon, LogoutIcon, MagnifyingGlassIcon } from '~/components/icons'
  import { BlurReveal } from '~/components/ui/blur-reveal'
  import { useServices } from '~/api/models'
  import { useQueryClient } from '@tanstack/vue-query'
  import { useAuthentication } from '~/composables/auth/useAuthentication'
  import { useReportDownloadsStore } from '~/stores/reportDownloadsStore'
  import { useOAuth } from '~/composables/auth/useOAuth'

  const searchText = ref<string>('')

  const services = useServices()
  const authStore = useAuthStore()
  const queryClient = useQueryClient()
  const assetStore = useAssetStore()
  const filterStore = useFilterStore()
  const interfaceStore = useInterfaceStore()
  const reportDownloadsStore = useReportDownloadsStore()
  const oauth = useOAuth()
  const { logUserOut } = useAuthentication()

  const { user, activeLicence, lastActiveLicence, isAuthenticated } = storeToRefs(authStore)
  const { data: licences, isLoading } = services.licences.useGetAllQuery()

  const filteredLicenses = computed<Licence[]>(() => {
    return searchText.value
      ? (licences.value || []).filter((item) =>
          item.name.toLowerCase().includes(String(searchText.value).toLowerCase())
        )
      : licences.value || []
  })

  const handleClearSearch = (): void => {
    searchText.value = ''
  }

  const handleLogout = (): void => {
    logUserOut()
    navigateTo(oauth.buildRedirectUrl('logout'), { external: true })
  }

  const handleSelectLicense = (licence: Licence): void => {
    authStore.setActiveLicence(licence)
    if (lastActiveLicence.value && lastActiveLicence.value.id === licence.id) return

    queryClient.clear()
    assetStore.$reset()
    interfaceStore.$reset()
    filterStore.$reset()
    reportDownloadsStore.$reset()
  }

  // The Component is never unmounted, so we need to handle cleanup manually
  watch(
    () => activeLicence.value,
    (newValue) => {
      if (newValue) {
        searchText.value = ''
      }
    }
  )
</script>

<template>
  <div v-if="!activeLicence && isAuthenticated" class="absolute top-0 left-0 h-screen w-full z-50 flex">
    <div
      class="w-full h-full absolute bg-primary/30 [mask-image:radial-gradient(900px_at_center,transparent_10%,white)] dark:bg-black backdrop-blur-sm"
    ></div>
    <div class="w-full h-full absolute bg-primary/30 bg backdrop-blur-sm"></div>
    <div class="w-full relative max-w-2xl mx-auto pt-[calc(35vh-200px)]">
      <BlurReveal :delay="0.2" :duration="0.75" class="py-8">
        <p class="text-xl text-white font-bold tracking-tighter xl:text-3xl/none sm:text-5xl">
          {{ $t('licences.welcomeBack') }}<span class="text-white">{{ user?.name }}</span>
        </p>
        <span class="text-pretty text-white text-xl tracking-tighter xl:text2xl/none sm:text-3xl font-light">
          {{ $t('licences.welcomeBackMessage') }}
        </span>
      </BlurReveal>
      <div class="glass-morphism rounded-lg p-2">
        <div class="relative">
          <InspiraInput
            v-model="searchText"
            :placeholder="$t('licences.searchPlaceholder')"
            autofocus
            class="w-full bg-white p-3 rounded-md text-sm focus:outline focus:outline-1 focus:-outline-offset-1 focus:outline-gray-500 px-8"
            @keyup.esc="handleClearSearch"
          />
          <div aria-hidden="true" class="absolute left-0 top-0 h-full flex items-center px-2 pointer-events-none">
            <MagnifyingGlassIcon class="size-5" />
          </div>
          <div
            v-if="searchText"
            aria-hidden="true"
            class="absolute right-0 top-0 h-full flex items-center px-2 text-gray-500 hover:text-gray-800 cursor-pointer"
            @click="handleClearSearch"
          >
            <CloseIcon class="size-4" />
          </div>
        </div>
      </div>

      <div class="mt-3">
        <div class="rounded-lg glass-morphism p-2">
          <div class="max-h-80 space-y-2 overflow-y-auto no-scrollbar">
            <div v-if="isLoading" class="text-sm p-4 rounded-md h-24 flex-col flex justify-center items-center">
              <div class="flex gap-2 items-center text-primary">
                <LoadingArrowsIcon class="size-6 animate-spin" />
                <p class="text-lg font-semibold">{{ $t('licences.loading') }}</p>
              </div>
            </div>
            <template v-if="!isLoading">
              <div
                v-for="licence in filteredLicenses"
                :key="licence.id"
                class="bg-white/80 active:scale-[98%] hover:bg-white p-3 rounded-md hover:opacity-100 text-sm cursor-pointer hover:shadow-md transition-all duration-300 text-primary border hover:border-primary"
                @click="handleSelectLicense(licence)"
              >
                <p class="font-semibold">{{ licence.name }}</p>
                <p v-if="licence.id === lastActiveLicence?.id" class="text-xs">{{ $t('licences.currentlyActive') }}</p>
              </div>
              <div
                v-if="!filteredLicenses.length"
                class="text-sm p-4 rounded-md h-24 flex-col flex justify-center text-center"
              >
                <p class="text-lg font-semibold text-primary">{{ $t('licences.noLicensesFound') }}</p>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="mt-8 flex justify-end">
        <Button class="text-primary text-sm" variant="outline" @click="handleLogout">
          <LogoutIcon class="size-4" />
          {{ $t('common.signOut') }}
        </Button>
      </div>
    </div>
  </div>
</template>

<style>
  .glass-morphism {
    position: relative;
    background: rgba(214, 214, 214, 0.3);
    box-shadow:
      0 1007px 281px 0 rgba(0, 0, 0, 0),
      0 644px 258px 0 rgba(0, 0, 0, 0.01),
      0 363px 219px 0 rgba(0, 0, 0, 0.05),
      0 160px 160px 0 rgba(0, 0, 0, 0.09),
      0 40px 88px 0 rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(12px);
    border-radius: 0.5rem;
  }

  .glass-morphism::before {
    content: '';
    position: absolute;
    inset: -1px;
    padding: 1px; /* border thickness */
    border-radius: 0.5rem;
    pointer-events: none;

    /* the shiny gradient border background */
    background: linear-gradient(to right, #f2f2f2, #818181 41%, #ffffff);

    /* --- WebKit (Safari/Chrome) longhands --- */
    -webkit-mask-image: linear-gradient(#fff 0 0), linear-gradient(#fff 0 0);
    -webkit-mask-origin: content-box, border-box;
    -webkit-mask-clip: content-box, border-box;
    /* create a hole (inner mask cut out of outer) */
    -webkit-mask-composite: xor;

    /* --- Standard longhands (Firefox, modern specs) --- */
    mask-image: linear-gradient(#fff 0 0), linear-gradient(#fff 0 0);
    mask-origin: content-box, border-box;
    mask-clip: content-box, border-box;
    mask-composite: exclude;
  }
</style>
